<template>
	<!-- Receiving account for transfers (phase 3 R-PAY, §6). Used by
	     PosSettings (B48, global) and the PosEventForm "บัญชีรับเงิน" tab (B49).
	     v-model = paymentAccountForm(...) shape; `bad` = field names the server
	     (or the client twin) rejected. -->
	<div class="row">
		<div :class="preview ? 'col-lg-7' : 'col-12'">
			<!-- 1. mode -->
			<div class="form-group">
				<label class="form-control-label d-block">{{ $t("pos.payment_account.mode") }} *</label>
				<el-radio-group :value="value.mode" class="pos-pa-modes" @input="(v) => set('mode', v)">
					<el-radio v-for="m in modes" :key="m" :label="m" class="d-block mb-2">{{ $t(`pos.payment_account.mode_${m}`) }}</el-radio>
				</el-radio-group>
				<div class="pos-pa-helper">{{ $t("pos.payment_account.mode_helper") }}</div>
			</div>
			<div class="row">
				<!-- 2. bank -->
				<div class="col-md-6 form-group">
					<label class="form-control-label d-block">{{ $t("pos.payment_account.bank") }} *</label>
					<el-select :value="value.bank_code" filterable class="w-100" @input="(v) => set('bank_code', v)">
						<el-option v-for="b in banks" :key="b.code" :label="b.name" :value="b.code"></el-option>
					</el-select>
					<small v-if="has('bank_code')" class="text-danger">{{ $t("pos.errors.pos_payment_account_invalid") }}</small>
				</div>
				<!-- 3. account no -->
				<div class="col-md-6 form-group">
					<label class="form-control-label d-block">{{ $t("pos.payment_account.account_no") }} *</label>
					<el-input :value="value.account_no" inputmode="numeric" maxlength="20" @input="(v) => set('account_no', v)" @blur="formatNo"></el-input>
					<small v-if="has('account_no')" class="text-danger">{{ $t("pos.payment_account.account_no_invalid") }}</small>
				</div>
				<!-- 4. account name -->
				<div class="col-12 form-group">
					<label class="form-control-label d-block">{{ $t("pos.payment_account.account_name") }} *</label>
					<el-input :value="value.account_name" maxlength="150" @input="(v) => set('account_name', v)"></el-input>
					<small v-if="has('account_name')" class="text-danger">{{ $t("pos.payment_account.account_name_required") }}</small>
				</div>
				<!-- 5. promptpay -->
				<template v-if="value.mode === 'promptpay'">
					<div class="col-md-6 form-group">
						<label class="form-control-label d-block">{{ $t("pos.payment_account.promptpay_type") }} *</label>
						<el-select :value="value.promptpay_type" class="w-100" @input="(v) => set('promptpay_type', v)">
							<el-option v-for="t in ppTypes" :key="t" :label="$t(`pos.payment_account.promptpay_type_${t}`)" :value="t"></el-option>
						</el-select>
					</div>
					<div class="col-md-6 form-group">
						<label class="form-control-label d-block">{{ $t("pos.payment_account.promptpay_id") }} *</label>
						<el-input :value="value.promptpay_id" inputmode="numeric" maxlength="20" @input="(v) => set('promptpay_id', v)"></el-input>
						<small v-if="ppIdError" class="text-danger">{{ ppIdError }}</small>
					</div>
				</template>
				<!-- 6. static QR image -->
				<div v-if="value.mode === 'static_qr_image'" class="col-12 form-group">
					<label class="form-control-label d-block">{{ $t("pos.payment_account.qr_image") }} *</label>
					<div class="d-flex align-items-start">
						<div class="pos-pa-qr mr-3">
							<img v-if="value.qr_image_data_url" :src="value.qr_image_data_url" alt="" />
						</div>
						<div>
							<label class="btn btn-sm btn-outline-primary mb-2">
								{{ value.qr_image_data_url ? $t("pos.payment_account.qr_change") : $t("pos.payment_account.qr_upload") }}
								<input type="file" accept="image/png,image/jpeg" class="d-none" @change="onQr" />
							</label>
							<small class="text-muted d-block">{{ $t("pos.payment_account.qr_hint") }}</small>
							<small v-if="has('qr_image_data_url')" class="text-danger d-block">{{ $t("pos.payment_account.qr_required") }}</small>
						</div>
					</div>
				</div>
				<!-- 7. show on receipt -->
				<div class="col-12 form-group d-flex align-items-center">
					<el-switch :value="value.show_on_receipt" class="mr-2" @input="(v) => set('show_on_receipt', v)"></el-switch>
					<span>{{ $t("pos.payment_account.show_on_receipt") }}</span>
				</div>
			</div>
		</div>
		<!-- live preview of the POS transfer tab (§5.3) for ฿1,692.00 -->
		<div v-if="preview" class="col-lg-5">
			<div class="pos-pa-preview">
				<div class="pos-pa-preview-title">{{ $t("pos.payment_account.preview") }}</div>
				<div class="pos-pa-amount-label">{{ $t("pos.payment_account.preview_amount") }}</div>
				<div class="pos-pa-amount">฿{{ money(previewAmount) }}</div>
				<div v-if="value.mode === 'promptpay' && qrPath" class="text-center">
					<svg class="pos-pa-qrsvg" :viewBox="`0 0 ${qrSize} ${qrSize}`" shape-rendering="crispEdges">
						<rect :width="qrSize" :height="qrSize" fill="#fff" />
						<path :d="qrPath" fill="#000" />
					</svg>
					<div class="small">{{ $t("pos.payment_account.preview_dynamic_qr") }}</div>
				</div>
				<div v-else-if="value.mode === 'static_qr_image' && value.qr_image_data_url" class="text-center">
					<img :src="value.qr_image_data_url" class="pos-pa-qrimg" alt="" />
					<div class="small">{{ $t("pos.payment_account.preview_no_amount") }}</div>
				</div>
				<div v-else class="small text-center mb-2">{{ $t("pos.payment_account.preview_no_qr") }}</div>
				<div class="pos-pa-bank">
					<div><strong>{{ bankLabel }}</strong></div>
					<div class="pos-pa-acc">{{ formattedNo }}</div>
					<div>{{ value.account_name }}</div>
				</div>
				<div v-if="value.show_on_receipt" class="pos-pa-receipt">
					<div class="small text-muted">{{ $t("pos.payment_account.receipt_line") }}</div>
					<div>{{ receiptText }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import swal from "sweetalert2";
import { RadioGroup, Radio, Select, Option, Input, Switch } from "element-ui";
import { qrMatrix } from "@/utils/qrcode";
import { posMoney, posResizeImageFile, posDataUrlBytes, POS_QR_IMAGE_MAX_SIDE, POS_QR_IMAGE_MAX_BYTES } from "@/utils/pos";
import {
	THAI_BANKS,
	PAY_MODES,
	PROMPTPAY_TYPES,
	formatBankAccount,
	digitsOnly,
	isThaiTaxIdValid,
	promptpayIdValid,
	promptPayPayload,
	paymentAccountText
} from "./posPaymentAccount";

export default {
	name: "PosPaymentAccountForm",
	components: {
		[RadioGroup.name]: RadioGroup,
		[Radio.name]: Radio,
		[Select.name]: Select,
		[Option.name]: Option,
		[Input.name]: Input,
		[Switch.name]: Switch
	},
	props: {
		value: { type: Object, required: true },
		bad: { type: Array, default: () => [] },
		preview: { type: Boolean, default: true }
	},
	data() {
		return { modes: PAY_MODES, ppTypes: PROMPTPAY_TYPES, previewAmount: 1692 };
	},
	computed: {
		banks() {
			return THAI_BANKS.map((b) => ({ code: b.code, name: this.$te(`pos.payment_account.banks.${b.code}`) ? this.$t(`pos.payment_account.banks.${b.code}`) : b.name }));
		},
		bankLabel() {
			const b = this.banks.find((x) => x.code === this.value.bank_code);
			return b ? b.name : "";
		},
		formattedNo() {
			return formatBankAccount(this.value.bank_code, this.value.account_no);
		},
		receiptText() {
			return paymentAccountText({ ...this.value, account_no: digitsOnly(this.value.account_no) });
		},
		ppIdError() {
			const id = digitsOnly(this.value.promptpay_id);
			if (!id && !this.has("promptpay_id")) return "";
			if (this.value.promptpay_type === "tax_id" && !isThaiTaxIdValid(id)) return this.$t("pos.payment_account.tax_id_invalid");
			if (!promptpayIdValid(this.value.promptpay_type, id) || this.has("promptpay_id")) return this.$t("pos.payment_account.promptpay_id_invalid");
			return "";
		},
		qr() {
			if (this.value.mode !== "promptpay" || !promptpayIdValid(this.value.promptpay_type, this.value.promptpay_id)) return null;
			const payload = promptPayPayload(this.value.promptpay_id, this.previewAmount);
			if (!payload) return null;
			try {
				return qrMatrix(payload, "MEDIUM");
			} catch (e) {
				return null;
			}
		},
		qrSize() {
			return this.qr ? this.qr.length + 2 : 0;
		},
		qrPath() {
			if (!this.qr) return "";
			let d = "";
			this.qr.forEach((row, y) => {
				row.forEach((on, x) => {
					if (on) d += `M${x + 1} ${y + 1}h1v1h-1z`;
				});
			});
			return d;
		}
	},
	methods: {
		money: posMoney,
		has(f) {
			return this.bad.indexOf(f) !== -1;
		},
		set(key, val) {
			this.$emit("input", { ...this.value, [key]: val });
		},
		formatNo() {
			this.set("account_no", formatBankAccount(this.value.bank_code, this.value.account_no));
		},
		async onQr(e) {
			const file = e.target.files && e.target.files[0];
			e.target.value = "";
			if (!file) return;
			try {
				// keep colour; PNG first, JPEG when PNG is too heavy
				let url = await posResizeImageFile(file, POS_QR_IMAGE_MAX_SIDE, "image/png");
				if (posDataUrlBytes(url) > POS_QR_IMAGE_MAX_BYTES) url = await posResizeImageFile(file, POS_QR_IMAGE_MAX_SIDE, "image/jpeg", 0.9);
				if (posDataUrlBytes(url) > POS_QR_IMAGE_MAX_BYTES) {
					swal.fire({ title: this.$t("pos.payment_account.qr_too_big"), icon: "warning" });
					return;
				}
				this.set("qr_image_data_url", url);
			} catch (err) {
				swal.fire({ title: this.$t("pos.errors.pos_qr_image_invalid"), icon: "warning" });
			}
		}
	}
};
</script>

<style>
.pos-pa-modes .el-radio {
	margin-left: 0 !important;
}
.pos-pa-helper {
	background: #f6f9fc;
	border-left: 3px solid #5e72e4;
	padding: 8px 12px;
	font-size: 13px;
	color: #525f7f;
	border-radius: 4px;
}
.pos-pa-qr {
	width: 120px;
	height: 120px;
	border-radius: 8px;
	background: #e9ecef;
	overflow: hidden;
	flex: none;
}
.pos-pa-qr img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}
.pos-pa-preview {
	border: 1px solid #dee2e6;
	border-radius: 12px;
	padding: 16px;
	max-width: 340px;
	margin: 0 auto;
	background: #fff;
}
.pos-pa-preview-title {
	font-weight: 600;
	margin-bottom: 8px;
	color: #32325d;
}
.pos-pa-amount-label {
	font-size: 13px;
	color: #525f7f;
}
.pos-pa-amount {
	font-size: 28px;
	font-weight: 700;
	color: #32325d;
	margin-bottom: 8px;
}
.pos-pa-qrsvg,
.pos-pa-qrimg {
	width: 180px;
	height: 180px;
	object-fit: contain;
}
.pos-pa-bank {
	margin-top: 12px;
	padding-top: 12px;
	border-top: 1px dashed #dee2e6;
	text-align: center;
	color: #32325d;
}
.pos-pa-acc {
	font: 600 20px/1.4 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	letter-spacing: 0.05em;
}
.pos-pa-receipt {
	margin-top: 12px;
	padding: 8px;
	background: #f6f9fc;
	border-radius: 6px;
	font-size: 13px;
	color: #32325d;
}
</style>
